// src/components/ANZEventOverview.js
import React from "react";
import { Container, Row, Col, Card } from "reactstrap";
import {
    Building2,
    Droplets,
    Zap,
    Waves,
    Network,
    Map,
    Globe,
    Users,
    CalendarCheck,
    ListChecks,
    CheckCircle2,
} from "lucide-react";

function ANZEventOverview() {
    const size =
        document.body.clientWidth >= 928 || document.documentElement.clientWidth >= 928
            ? true
            : false;

    const overviewBullets = [
        "Compact, transit-oriented urban growth",
        "Integrated housing and infrastructure planning",
        "Climate-resilient water and wastewater services",
        "Clean-energy and smart-grid transitions",
        "Evidence-driven spatial planning under evolving resource management frameworks",
        "Trans-Tasman climate and digital innovation partnerships",
    ];

    const impactCards = [
        {
            icon: Building2,
            title: "Cities & Infrastructure",
            text:
                "A once-in-a-generation reshape of cities, water networks and energy systems aligned to a more resilient future.",
        },
        {
            icon: Droplets,
            title: "Water & Utilities",
            text:
                "Digital twins and AI modernising treatment plants, cleaning legacy asset data and enabling predictive maintenance.",
        },
        {
            icon: Zap,
            title: "Energy & Smart Grids",
            text:
                "Smart-grid and electrification initiatives supported by high-quality, real-time data from digital twins.",
        },
        {
            icon: Waves,
            title: "Climate Resilience",
            text:
                "Pilots across wastewater, flooding and smart irrigation sharpening investment, resilience and climate decisions.",
        },
    ];

    const audienceCards = [
        {
            icon: Users,
            title: "Who It Connects",
            text:
                "Infrastructure owners, urban planners, utilities, Māori and community leaders, technologists and researchers across ANZ.",
        },
        {
            icon: Network,
            title: "Strategic Platform",
            text:
                "A region-wide forum for collaboration on digital twins, AI and advanced data infrastructure.",
        },
        {
            icon: Map,
            title: "Spatial Intelligence",
            text:
                "Evidence-driven spatial planning under evolving resource management frameworks.",
        },
        {
            icon: Globe,
            title: "Trans-Tasman Vision",
            text:
                "Climate and digital innovation partnerships linking Aotearoa New Zealand with the broader ANZ region.",
        },
    ];

    return (
        <div
            className="section"
            style={{
                background: "transparent",
                position: "relative",
                zIndex: 1,
            }}
        >
            <Container>
                {/* Heading */}
                <Row className="justify-content-center mb-5">
                    <Col className="ml-auto mr-auto text-center" md={12}>
                        <h1
                            className="text-center text-g mb-3"
                            style={{
                                fontSize: size ? "3.5rem" : "2rem",
                                fontWeight: "700",
                                letterSpacing: "0.5px",
                                background: "linear-gradient(135deg, #6bbac2 0%, #bfbe28 100%)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text",
                            }}
                        >
                            Event Overview
                        </h1>
                        <div
                            style={{
                                width: "120px",
                                height: "4px",
                                background: "linear-gradient(90deg, #6bbac2, #bfbe28)",
                                borderRadius: "2px",
                                margin: "0 auto 3rem",
                            }}
                        />
                    </Col>
                </Row>

                {/* Two-column narrative + key impacts */}
                <Row className="justify-content-center mb-5">
                    <Col md={6} className="mb-4">
                        <Card
                            style={{
                                background: "rgba(255, 255, 255, 0.05)",
                                backdropFilter: "blur(10px)",
                                border: "1px solid rgba(255, 255, 255, 0.1)",
                                borderRadius: "12px",
                                padding: size ? "2rem" : "1.5rem",
                                height: "100%",
                                transition: "all 0.3s ease",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = "rgba(255, 255, 255, 0.08)";
                                e.currentTarget.style.transform = "translateY(-5px)";
                                e.currentTarget.style.boxShadow = "0 10px 30px rgba(107, 186, 194, 0.3)";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)";
                                e.currentTarget.style.transform = "translateY(0)";
                                e.currentTarget.style.boxShadow = "none";
                            }}
                        >
                            <p
                                className="text-400 text-white mb-4"
                                style={{
                                    fontSize: size ? "1.1rem" : "0.95rem",
                                    lineHeight: "1.8",
                                }}
                            >
                                Aotearoa New Zealand is in the middle of a once-in-a-generation transformation of its
                                cities, water networks and energy systems. From housing and planning reform to Local
                                Water Done Well, smart grids and world-class fibre, the country is aligning policy,
                                infrastructure and digital capability to build a more resilient future.
                            </p>
                            <p
                                className="text-400 text-white mb-0"
                                style={{
                                    fontSize: size ? "1.1rem" : "0.95rem",
                                    lineHeight: "1.8",
                                }}
                            >
                                Utilities and councils are already deploying Digital Twins and AI to modernise water
                                treatment plants, clean up legacy asset data and move from reactive repairs to
                                predictive maintenance. Pilots across wastewater, flooding and smart irrigation are
                                demonstrating how virtual replicas of infrastructure can sharpen investment,
                                resilience and climate decisions.
                            </p>
                        </Card>
                    </Col>

                    <Col md={6} className="mb-4">
                        <Row>
                            {impactCards.map((item, i) => (
                                <Col xs={12} sm={6} key={i} className="mb-4">
                                    <Card
                                        style={{
                                            background: "rgba(255, 255, 255, 0.05)",
                                            backdropFilter: "blur(10px)",
                                            border: "1px solid rgba(255, 255, 255, 0.1)",
                                            borderRadius: "16px",
                                            padding: size ? "1.5rem" : "1rem",
                                            height: "100%",
                                            transition: "all 0.3s ease",
                                            cursor: "pointer",
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = "rgba(107, 186, 194, 0.12)";
                                            e.currentTarget.style.borderColor = "rgba(107, 186, 194, 0.5)";
                                            e.currentTarget.style.transform = "translateY(-4px)";
                                            e.currentTarget.style.boxShadow =
                                                "0 10px 25px rgba(107, 186, 194, 0.25)";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)";
                                            e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.1)";
                                            e.currentTarget.style.transform = "translateY(0)";
                                            e.currentTarget.style.boxShadow = "none";
                                        }}
                                    >
                                        <div className="d-flex flex-column align-items-start">
                                            <div
                                                style={{
                                                    width: 52,
                                                    height: 52,
                                                    borderRadius: "16px",
                                                    background:
                                                        "linear-gradient(135deg, rgba(107,186,194,0.22), rgba(191,190,40,0.18))",
                                                    border: "2px solid rgba(107,186,194,0.4)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    marginBottom: "1rem",
                                                    boxShadow: "0 4px 15px rgba(107,186,194,0.25)",
                                                }}
                                            >
                                                {React.createElement(item.icon, {
                                                    size: 26,
                                                    strokeWidth: 2.5,
                                                    style: { color: "#6bbac2" },
                                                })}
                                            </div>
                                            <h5
                                                className="text-white mb-2"
                                                style={{ fontSize: "0.95rem", fontWeight: 600 }}
                                            >
                                                {item.title}
                                            </h5>
                                            <p
                                                className="text-400 text-white mb-0"
                                                style={{ fontSize: "0.85rem", lineHeight: 1.6 }}
                                            >
                                                {item.text}
                                            </p>
                                        </div>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </Col>
                </Row>


            </Container>
        </div>
    );
}

export default ANZEventOverview;